import { Timestamp } from '../interfaces';

// When MS Serial starts
const EPOCH = 25569;
export const DAY_SECONDS = 86400;
export const MILLISECONDS = 1000;

/**
 * Excel has their own calculations for determining what the serial number should be and how it
 * converts to a real date. Essentially the serial number represents the number of days that has passed
 * since January 1st, 1900. So 1/1/1900 serial value would be 1.
 *
 * @docs https://support.microsoft.com/en-us/office/datevalue-function-df8b07d4-7761-4a93-bc33-b7471bbff252
 *
 * @param serial the serial number that excel generates
 * @returns the serial number converted to the corresponding date it represents
 */

export function excelSerialToJsDate(serial: number) {
	const daysSinceEpoch = serial - EPOCH;
	const millisecondsSinceEpoch = daysSinceEpoch * DAY_SECONDS * MILLISECONDS;
	return new Date(millisecondsSinceEpoch);
}

export const isDateBetween = <T = Date | Timestamp | number>(
	value: T,
	options: { min: T; max: T },
) => options.min <= value && value <= options.max;

/**
 * Date-like object with year and month (0-indexed).
 * Compatible with TuiDay, native Date, and similar types.
 */
export interface DateLikeWithMonth {
	year: number;
	month: number;
}

/**
 * Checks whether a date range spans exactly the given number of months.
 *
 * @param start - Start date (year, month)
 * @param end - End date (year, month)
 * @param months - Number of months to compare against (e.g. 11 for inclusive full year like Jan 1–Dec 31)
 * @returns true if the month difference equals the given value
 */
export function dateRangeMonthCount(
	start: DateLikeWithMonth,
	end: DateLikeWithMonth,
): number {
	const monthsDiff = (end.year - start.year) * 12 + (end.month - start.month);
	const monthsIncluded = monthsDiff + 1;
	return monthsIncluded;
}

export function dateRangeSpansMonths(
	start: DateLikeWithMonth,
	end: DateLikeWithMonth,
	months: number,
): boolean {
	return dateRangeMonthCount(start, end) === months;
}

export function toDateLike(d: Date): DateLikeWithMonth {
	return { year: d.getFullYear(), month: d.getMonth() };
}

/**
 * Calendar year/month from a UTC-stored date (e.g. Firestore timestamps saved via TuiDay.toUtcNativeDate).
 */
export function toUtcDateLike(d: Date): DateLikeWithMonth {
	return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
}

const DEFAULT_UTC_DATE_FORMAT: Intl.DateTimeFormatOptions = {
	month: 'short',
	day: 'numeric',
	year: 'numeric',
	timeZone: 'utc',
};

/**
 * Formats a UTC-stored date for display without local timezone shifting the calendar day.
 */
export function formatUtcDate(
	d: Date,
	options: Intl.DateTimeFormatOptions = DEFAULT_UTC_DATE_FORMAT,
): string {
	return d.toLocaleDateString('en-US', options);
}
